import { Controller } from "@hotwired/stimulus";

/**
 * Audit Continue Button Controller (Restaurant Audit).
 *
 * Each per-step canvas mounts its own continue button
 * with `stepKeyValue` set to that step's key. The
 * controller does TWO things:
 *
 * 1. Visibility: hides the button unless its step is
 *    the currently-active stage. Hides on
 *    `reading_menu` / `painting_demo` (they have their
 *    own affordances or are terminal). Hides on
 *    `auditStatus === "failed"`. Listens to
 *    `audit-stage:transition` for these signals.
 *
 * 2. Enabled: disabled by default. Each step's own
 *    Stimulus controller dispatches
 *    `audit-step:complete` (with `{stepKey}` in the
 *    detail) when its visible content is ready (data
 *    on screen, animations played, whatever the step
 *    considers "done"). audit-continue-button listens
 *    for THAT event and flips disabled=false on the
 *    matching button.
 *
 * On click, dispatches `audit-continue:click` on the
 * document with `{fromKey}`. audit-stage advances.
 */
const HIDDEN_KEYS = new Set([
  "reading_menu",
  "painting_demo",
]);

export default class extends Controller {
  static targets = ["button"];
  static values = {
    stepKey: String,
    // "always-visible": the button stays visible
    // regardless of which step is active (used by
    // the report's bottom CTA, which is part of the
    // reading_menu canvas and always visible while
    // the user is on it). Only the `disabled` state
    // gets toggled on `audit-step:complete`.
    visibility: { type: String, default: "" },
  };

  connect() {
    this.boundHandleTransition =
      this.handleTransition.bind(this);
    this.boundHandleStepComplete =
      this.handleStepComplete.bind(this);

    document.addEventListener(
      "audit-stage:transition",
      this.boundHandleTransition
    );
    document.addEventListener(
      "audit-step:complete",
      this.boundHandleStepComplete
    );

    // If the step has already finished by the time
    // this controller mounted (page loaded into a
    // done step), enable immediately. Each step's
    // controller stamps `data-audit-step-complete`
    // on its stage when it dispatches the event.
    const stage = this.element.closest(
      '[data-audit-stage-target="stage"]'
    );
    if (
      stage?.dataset.auditStepComplete === "true"
    ) {
      this.enable();
    }

    this.syncVisibility();
  }

  disconnect() {
    document.removeEventListener(
      "audit-stage:transition",
      this.boundHandleTransition
    );
    document.removeEventListener(
      "audit-step:complete",
      this.boundHandleStepComplete
    );
  }

  handleTransition(event) {
    this.syncVisibility(event.detail || {});
  }

  handleStepComplete(event) {
    if (
      event.detail?.stepKey === this.stepKeyValue
    ) {
      this.enable();
    }
  }

  enable() {
    if (!this.hasButtonTarget) return;
    this.buttonTarget.disabled = false;
  }

  syncVisibility(detail) {
    if (!this.hasButtonTarget) return;
    if (this.visibilityValue === "always-visible") {
      // Report bottom CTA: never toggle hidden;
      // it's part of a canvas that's always visible
      // while the user is on it. Only `disabled`
      // gets toggled, by the `audit-step:complete`
      // event for its target step.
      return;
    }
    const btn = this.buttonTarget;

    const state = detail || this.stateFromDom();
    if (!state) return;

    const isMine =
      state.currentKey === this.stepKeyValue;
    const hidden =
      !isMine ||
      HIDDEN_KEYS.has(this.stepKeyValue) ||
      state.auditStatus === "failed";

    if (hidden) {
      btn.classList.add("hidden");
    } else {
      btn.classList.remove("hidden");
    }
  }

  stateFromDom() {
    const stage = document.querySelector(
      '[data-controller~="audit-stage"]'
    );
    if (!stage) return null;
    const activeStage = stage.querySelector(
      '[data-audit-stage-target="stage"]' +
        "[data-audit-stage-active]"
    );
    if (!activeStage) return null;
    return {
      currentKey:
        activeStage.dataset.auditStageKey,
      auditStatus:
        stage.dataset.auditStageAuditStatusValue,
    };
  }

  advance(event) {
    event.preventDefault();
    if (this.buttonTarget.disabled) return;
    document.dispatchEvent(
      new CustomEvent("audit-continue:click", {
        detail: {fromKey: this.stepKeyValue},
      })
    );
  }
}
